import React, { useEffect, useState } from 'react';
import { Table, Card, Spinner, Alert, Row, Col, Badge } from 'react-bootstrap';
import { Link } from 'react-router-dom';
import { BsBarChartLine, BsChatDots, BsEmojiSmile, BsEmojiFrown } from 'react-icons/bs';
import AdminLayout from '../components/AdminLayout';
import api from '../lib/api';

function WidgetAnalyticsPage() {
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const fetchAnalytics = async () => {
    try {
      setLoading(true);
      setError('');
      const res = await api.get('/admin/widgets/analytics/');
      setData(Array.isArray(res.data) ? res.data : []);
    } catch (err) {
      setError('Failed to fetch widget analytics.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchAnalytics();
  }, []);

  const totals = data.reduce(
    (acc, item) => {
      acc.chats += Number(item.total_chats || 0);
      acc.positive += Number(item.positive || 0);
      acc.negative += Number(item.negative || 0);
      return acc;
    },
    { chats: 0, positive: 0, negative: 0 }
  );

  return (
    <AdminLayout
      title="Widget Analytics"
      subtitle="Track widget performance, usage volume, and conversation feedback across all deployed widget sites."
    >
      {error ? <Alert variant="danger">{error}</Alert> : null}

      <Row className="g-3 mb-4">
        <Col md={4}>
          <div className="admin-stat-card">
            <div className="d-flex align-items-start justify-content-between gap-3">
              <div>
                <div className="text-muted small fw-semibold mb-2">Total Chats</div>
                <h3 className="mb-0 fw-bold">{totals.chats}</h3>
              </div>
              <div className="admin-stat-card__icon">
                <BsChatDots />
              </div>
            </div>
          </div>
        </Col>

        <Col md={4}>
          <div className="admin-stat-card">
            <div className="d-flex align-items-start justify-content-between gap-3">
              <div>
                <div className="text-muted small fw-semibold mb-2">Positive Feedback</div>
                <h3 className="mb-0 fw-bold">{totals.positive}</h3>
              </div>
              <div className="admin-stat-card__icon">
                <BsEmojiSmile />
              </div>
            </div>
          </div>
        </Col>

        <Col md={4}>
          <div className="admin-stat-card">
            <div className="d-flex align-items-start justify-content-between gap-3">
              <div>
                <div className="text-muted small fw-semibold mb-2">Negative Feedback</div>
                <h3 className="mb-0 fw-bold">{totals.negative}</h3>
              </div>
              <div className="admin-stat-card__icon">
                <BsEmojiFrown />
              </div>
            </div>
          </div>
        </Col>
      </Row>

      <Card className="admin-surface-card">
        <Card.Body>
          <div className="d-flex align-items-center gap-2 mb-3">
            <BsBarChartLine className="fs-4 text-primary" />
            <h5 className="mb-0 fw-bold">Widget Performance Table</h5>
          </div>

          {loading ? (
            <div className="text-center py-5">
              <Spinner animation="border" />
            </div>
          ) : (
            <div className="table-responsive">
              <Table hover className="admin-table align-middle mb-0">
                <thead>
                  <tr>
                    <th>Widget</th>
                    <th>Token</th>
                    <th>Total Chats</th>
                    <th>Positive</th>
                    <th>Negative</th>
                    <th>Last Used</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {data.length ? (
                    data.map((item) => (
                      <tr key={item.token}>
                        <td>{item.site}</td>
                        <td style={{ fontSize: '0.85rem' }}>
                          <code>{item.token}</code>
                        </td>
                        <td>
                          <Badge bg="primary" className="px-3 py-2 rounded-pill">
                            {item.total_chats}
                          </Badge>
                        </td>
                        <td>{item.positive}</td>
                        <td>{item.negative}</td>
                        <td>
                          {item.last_activity
                            ? new Date(item.last_activity).toLocaleString()
                            : '—'}
                        </td>
                        <td>
                          <div className="d-flex gap-2 flex-wrap">
                            <Link
                              to={`/widgets/${item.token}/analytics`}
                              className="btn btn-sm btn-outline-success admin-action-btn"
                            >
                              Analytics
                            </Link>
                            <Link
                              to={`/widgets/${item.token}/chats`}
                              className="btn btn-sm btn-outline-dark admin-action-btn"
                            >
                              Chats
                            </Link>
                          </div>
                        </td>
                      </tr>
                    ))
                  ) : (
                    <tr>
                      <td colSpan={7} className="text-center py-4">
                        No analytics data available.
                      </td>
                    </tr>
                  )}
                </tbody>
              </Table>
            </div>
          )}
        </Card.Body>
      </Card>
    </AdminLayout>
  );
}

export default WidgetAnalyticsPage;